import React, { useEffect, useState } from 'react';
import { QueryResult, useQuery } from '@apollo/client';
import { fetchCloudSitesForWidgetPPC } from './queries/fetch-cloud-sites-for-txa-for-widget';
import {
  FetchCloudSitesForWidgetPpcQuery,
  FetchCloudSitesForWidgetPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { RecentlyViewedCloudSiteMetadata } from '@css-xp/ppc-types';
import { RecentlyViewedData } from '@atlassian/cssxp-typescommon';
import { CloudSiteForWidget, CloudSiteQueryItem } from './types';

type CloudSitesNodesMap = { [cloudSiteId: string]: CloudSiteQueryItem };
type RecentlyViewedCloudSiteData = RecentlyViewedData<RecentlyViewedCloudSiteMetadata>;
type RecentViewedDataNodesMap = { [data: string]: RecentlyViewedCloudSiteData };

type ChildrenProp = (
  args: Omit<QueryResult<FetchCloudSitesForWidgetPpcQuery, FetchCloudSitesForWidgetPpcQueryVariables>, 'data'> & {
    data: CloudSiteForWidget[] | null | undefined;
  }
) => JSX.Element;

export const RecentlyViewedCloudSitesQueryWrapper = ({
  txaId,
  recentlyViewedData,
  children,
}: {
  txaId: string;
  children: ChildrenProp;
  recentlyViewedData: RecentlyViewedCloudSiteData[];
}): JSX.Element => {
  const [recentlyViewedWidgetData, setRecentlyViewedWidgetData] = useState<CloudSiteForWidget[]>();

  const recentlyViewedDataNodesMap: RecentViewedDataNodesMap = React.useMemo(
    () =>
      recentlyViewedData.reduce((acc: RecentViewedDataNodesMap, recentData: RecentlyViewedCloudSiteData) => {
        if (!recentData.metadata || !recentData.metadata.id) {
          return acc;
        }
        acc[recentData.metadata.id] = recentData;
        return acc;
      }, {}),
    [recentlyViewedData]
  );

  const cloudSiteIds = React.useMemo(() => Object.keys(recentlyViewedDataNodesMap), [recentlyViewedDataNodesMap]);

  const { data, ...args } = useQuery<FetchCloudSitesForWidgetPpcQuery, FetchCloudSitesForWidgetPpcQueryVariables>(
    fetchCloudSitesForWidgetPPC,
    {
      variables: {
        params: {
          idIsIn: cloudSiteIds,
          transactionAccountId: txaId,
        },
      },
      skip: !txaId || !cloudSiteIds || !cloudSiteIds.length,
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    const nodes = data?.cloudLicenses?.nodes;
    if (!nodes || !nodes.length) {
      return;
    }
    const cloudSitesNodesMap: CloudSitesNodesMap = nodes.reduce(
      (acc: CloudSitesNodesMap, node: CloudSiteQueryItem | null) => {
        if (!node || !node.cloudSiteId) {
          return acc;
        }
        acc[node.cloudSiteId] = node;
        return acc;
      },
      {}
    );
    // making the nodes order same as it's passed from props and re-assigning it
    const productNodes = cloudSiteIds
      .filter((id) => !!cloudSitesNodesMap[id])
      .map((id) => ({
        ...cloudSitesNodesMap[id],
        timestamp: recentlyViewedDataNodesMap[id].timestamp,
        transactionAccountId: txaId,
        companyName: cloudSitesNodesMap[id]?.shipToParty?.name,
      }));

    setRecentlyViewedWidgetData(productNodes);
  }, [data, recentlyViewedData]);

  return children({
    data: recentlyViewedWidgetData,
    ...args,
  });
};
